import React, { useMemo } from 'react';

import { PartialPartial } from '@paragon/utilities';

import { FormattedTextField, FormattedTextFieldProps } from './FormattedTextField';

interface OwnProps {
  min?: number;
  max?: number;

  integers?: boolean;

  /**
   * Maximum number of decimals to display; this does not pad with zeros.
   */
  numberOfDecimals?: number;

  /**
   * Pad with zeroes if `numberOfDecimals` is set
   */
  padWithZeros?: boolean;
}

export type NumericFieldProps<
  TAllowNull extends boolean = false,
  TAllowNullSubmittal extends boolean = false,
  TAllowIndeterminate extends boolean = false,
> = PartialPartial<
  FormattedTextFieldProps<number, TAllowNull, TAllowNullSubmittal, TAllowIndeterminate>,
  'formatValue' | 'parseValue'
> &
  OwnProps;

export function NumericField<
  TAllowNull extends boolean = false,
  TAllowNullSubmittal extends boolean = false,
  TAllowIndeterminate extends boolean = false,
>(props: NumericFieldProps<TAllowNull, TAllowNullSubmittal, TAllowIndeterminate>) {
  const { min, max, integers, numberOfDecimals, padWithZeros, ...fieldProps } = props;
  const { formatValue, validators: providedValidators } = fieldProps;

  const validators = useMemo(() => {
    const result = providedValidators ? providedValidators.slice() : [];

    result.push((value: number) => {
      if (Number.isNaN(value)) {
        return 'not a number';
      }
    });

    if (min !== undefined) {
      result.push((value: number) => {
        if (value < min) {
          return `must be at least ${formatValue ? formatValue(min) : min}`;
        }
      });
    }

    if (max !== undefined) {
      result.push((value: number) => {
        if (value > max) {
          return `must be no more than ${formatValue ? formatValue(max) : max}`;
        }
      });
    }

    if (integers === true) {
      result.push((value: number) => {
        if (!Number.isInteger(value)) {
          return 'must be a whole number';
        }
      });
    }

    return result;
  }, [min, max, integers, formatValue, providedValidators]);

  return (
    <FormattedTextField<number, TAllowNull, TAllowNullSubmittal, TAllowIndeterminate>
      parseValue={numericFieldDefaultParseValue}
      formatValue={(value) => numericFieldDefaultFormatValue(value, { numberOfDecimals, padWithZeros })}
      {...fieldProps} // spread after format-related props to allow overriding
      validators={validators}
    />
  );
}

export function numericFieldDefaultParseValue(input: string): { valid: false } | { valid: true; value: number } {
  const result = parseFloat(input);
  return Number.isNaN(result) ? { valid: false } : { valid: true, value: result };
}

export function numericFieldDefaultFormatValue(
  num: number,
  options: { numberOfDecimals?: number; padWithZeros?: boolean },
): string {
  if (options.numberOfDecimals === undefined) return num.toString();
  if (options.padWithZeros !== undefined) return num.toFixed(options.numberOfDecimals);

  const powerOfTen = 10 ** options.numberOfDecimals;
  return (Math.round(num * powerOfTen) / powerOfTen).toString();
}
